import React, { useMemo } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { autocompletion } from '@codemirror/autocomplete';
import { indentOnInput, bracketMatching } from '@codemirror/language';
import { liquid } from '@codemirror/lang-liquid';
import { html } from '@codemirror/lang-html';
import classnames from 'classnames';
import { Collapse } from '@nerdgeschoss/shimmer-component-collapse';
import { t } from '../../../locales/translate';
import './form_html_editor.scss';
import { FormField } from '../../../model/form';

interface Props extends FormField<string> {
  value: string;
  onChange: (value: string) => void;
  height?: string;
  liquidVariables?: string[];
}

export function FormHtmlEditor({
  value,
  onChange,
  onBlur,
  placeholder,
  disabled,
  label,
  required,
  errors,
  touched,
  valid,
  height,
  liquidVariables,
}: Props): JSX.Element {
  const displayErrors = !valid && touched;

  const extensions = useMemo(() => {
    const variables = liquidVariables?.map((variable) => ({ label: variable, type: 'variable' })) || [];

    const htmlExtension = html();
    const extensions = [
      htmlExtension,
      liquid({ base: htmlExtension, variables: variables }),
      autocompletion(),
      indentOnInput(),
      bracketMatching(),
    ];

    return extensions;
  }, [JSON.stringify(liquidVariables)]);

  return (
    <div className="form-html-editor">
      {label && (
        <label className="form-html-editor__label">
          {label}
          {required && <span className="form-html-editor__required"> *</span>}
        </label>
      )}
      <div
        className={classnames('form-html-editor__wrapper', {
          'form-html-editor__wrapper--disabled': disabled,
          'form-html-editor__wrapper--error': displayErrors,
        })}
      >
        <CodeMirror
          height={height}
          value={value}
          placeholder={placeholder}
          extensions={extensions}
          editable={!disabled}
          onChange={onChange}
          onBlur={onBlur}
          className={classnames({ 'cm-editor-disabled': disabled })}
        />
      </div>
      <Collapse open={displayErrors}>
        {() =>
          errors?.map((error) => (
            <div key={error} className="form-html-editor__error">
              {t.errors[error] || error}
            </div>
          ))
        }
      </Collapse>
    </div>
  );
}
